import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import { MessageSquare, CheckCircle2, AlertTriangle, Smile, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRefs } from "@/lib/useRefs";
import { PageHeader, EmptyState } from "@/components/Common";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const COLORS = ["#192A53", "#A1C6EA", "#25604d", "#7a5a12", "#5b2a4a", "#a23a2a"];

function Kpi({ icon: Icon, label, value, suffix, tone }) {
  return (
    <div className="kp-card p-5">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${tone}`}><Icon className="w-4 h-4" /></div>
      <p className="font-display text-4xl text-foreground tabular-nums leading-none">{value}{suffix}</p>
      <p className="text-sm text-muted-foreground mt-2">{label}</p>
    </div>
  );
}

export default function Analytics() {
  const { user } = useAuth();
  const refs = useRefs();
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  const [prog, setProg] = useState("all");

  const isManager = user?.is_admin || user?.role_category === "rc_approver";

  useEffect(() => {
    if (!isManager) return;
    setD(null); setError(false);
    api.get("/analytics", { params: prog === "all" ? {} : { program: prog } })
      .then((r) => setD(r.data)).catch(() => setError(true));
  }, [prog, isManager]);

  if (!isManager) return <Navigate to="/app/dashboard" replace />;

  const managed = refs.managedPrograms;
  const filter = (
    <Select value={prog} onValueChange={setProg}>
      <SelectTrigger className="w-56 rounded-full bg-card" data-testid="analytics-program-filter"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{user?.is_admin ? "All programs" : "All my programs"}</SelectItem>
        {managed.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  if (error) return (
    <div>
      <PageHeader eyebrow="Console · Analytics" title="Analytics" subtitle="Usage, knowledge gaps, deflection and satisfaction." testid="analytics-header">{filter}</PageHeader>
      <EmptyState icon={AlertTriangle} title="Unable to load analytics" hint="Please try again later." />
    </div>
  );
  if (!d) return (
    <div>
      <PageHeader eyebrow="Console · Analytics" title="Analytics" subtitle="Usage, knowledge gaps, deflection and satisfaction." testid="analytics-header">{filter}</PageHeader>
      <div className="text-muted-foreground kp-fade">Loading analytics…</div>
    </div>
  );

  const roleData = d.usage_by_role.map((r) => ({ name: r.role.replace("rc_", ""), value: r.count }));
  const fbData = [{ name: "Helpful", value: d.feedback_up }, { name: "Not helpful", value: d.feedback_down }];
  const hasData = d.total_queries > 0;

  return (
    <div>
      <PageHeader
        eyebrow="Console · Analytics"
        title="Analytics"
        subtitle="Usage, knowledge gaps, deflection and satisfaction across the assistant."
        testid="analytics-header">{filter}</PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10 stagger" data-testid="analytics-kpis">
        <Kpi icon={MessageSquare} label="Total Queries" value={d.total_queries} tone="bg-secondary/70 text-primary" />
        <Kpi icon={CheckCircle2} label="Deflection Rate" value={d.deflection_rate} suffix="%" tone="bg-[#d9f2e5] text-[#25604d]" />
        <Kpi icon={AlertTriangle} label="Unanswered" value={d.unanswered_count} tone="bg-[#fbe0d9] text-[#a23a2a]" />
        <Kpi icon={Smile} label="Satisfaction" value={d.satisfaction} suffix="%" tone="bg-[#fdf2c9] text-[#7a5a12]" />
      </div>

      {!hasData && (
        <div className="mb-8"><EmptyState icon={TrendingUp} title="No usage data yet" hint="Ask the AI assistant a few questions to populate analytics." /></div>
      )}

      <div className="grid lg:grid-cols-2 gap-5 mb-5">
        <div className="kp-card p-6">
          <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-1">Distribution</p>
          <h3 className="font-display text-xl text-foreground mb-5">Usage by Role</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={roleData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(40 27% 87%)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "hsl(230 11% 40%)" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "hsl(230 11% 40%)" }} />
              <Tooltip contentStyle={{ background: "hsl(40 55% 98%)", border: "1px solid hsl(40 27% 87%)", borderRadius: 8 }} />
              <Bar dataKey="value" fill="#192A53" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="kp-card p-6">
          <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-1">Sentiment</p>
          <h3 className="font-display text-xl text-foreground mb-5">User Feedback</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={fbData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                {fbData.map((e, i) => <Cell key={i} fill={i === 0 ? "#25604d" : "#a23a2a"} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "hsl(40 55% 98%)", border: "1px solid hsl(40 27% 87%)", borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-6 text-xs font-medium">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#25604d]" /> Helpful ({d.feedback_up})</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#a23a2a]" /> Not helpful ({d.feedback_down})</span>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="kp-card p-6" data-testid="analytics-faq-table">
          <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-1">Most asked</p>
          <h3 className="font-display text-xl text-foreground mb-4">Top Questions</h3>
          {d.top_faqs.length === 0 ? <p className="text-sm text-muted-foreground italic">No questions yet.</p> : (
            <div className="space-y-2.5">
              {d.top_faqs.map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <span className="w-6 h-6 rounded-md bg-secondary/60 text-primary text-xs font-mono flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="text-foreground/80 truncate flex-1">{f.query}</span>
                  <span className="text-xs font-mono text-muted-foreground">{f.count}×</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="kp-card p-6">
          <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-1">Coverage gaps</p>
          <h3 className="font-display text-xl text-foreground mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-destructive" /> Knowledge Gaps
          </h3>
          {d.knowledge_gaps.length === 0 ? <p className="text-sm text-muted-foreground italic">No unanswered questions — great coverage!</p> : (
            <div className="space-y-2.5">
              {d.knowledge_gaps.map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <span className="text-foreground/80 truncate flex-1">{f.query}</span>
                  <span className="text-xs font-mono text-destructive shrink-0">{f.count}× unanswered</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
